import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";

interface ProtectedRouteProps {
  children: ReactNode;
  requireRole?: string;
}

export const ProtectedRoute = ({ children, requireRole }: ProtectedRouteProps) => {
  const { user, loading, hasRole } = useAuth();
  const { isTrialExpired, canAccessDashboard, loading: trialLoading } = useTrialStatus();
  const location = useLocation();
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(true);

  useEffect(() => {
    if (!user) {
      setCheckingVerification(false);
      return;
    }

    // Use email_confirmed_at from auth.users as single source of truth
    const isVerified = !!user.email_confirmed_at;
    setEmailVerified(isVerified);
    setCheckingVerification(false);
  }, [user]);

  if (loading || trialLoading || checkingVerification) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if email is verified (site admins bypass this)
  const isSiteAdmin = hasRole('site_admin');
  if (!isSiteAdmin && emailVerified === false) {
    return <Navigate to={`/check-email?email=${encodeURIComponent(user.email || '')}`} replace />;
  }

  // Check trial status and restrict access if trial expired
  if (isTrialExpired && !canAccessDashboard(location.pathname)) {
    return <Navigate to="/trial-expired" replace />;
  }

  // Site admins have access to everything
  if (requireRole && !isSiteAdmin && !hasRole(requireRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
